// Resumable/chunked upload (tus.io protocol) for a single candidate's CAD
// work file - CAD Phase 2. Talks to CadResumableUploadController on the
// backend (see that class and TusUploadConfig for the server side).
//
// Unlike the plain multipart path (still used everywhere else - see
// resilientUpload.js), this is genuinely resumable: tus-js-client chunks
// the file itself, retries transient failures automatically
// (retryDelays), and - the actual point of Phase 2 - can find and resume
// an unfinished upload for the exact same file after a full page reload
// (findPreviousUploads/resumeFromPreviousUpload) instead of restarting a
// large transfer from byte zero.
import { Upload } from 'tus-js-client';

// A chunk has to fully transfer within nginx's proxy_read_timeout/
// proxy_send_timeout for /api/tus-uploads/ or the request gets cut off
// mid-chunk. That's now 300s there specifically (see deploy/nginx.conf
// and the matching change on the live server), not the site-wide 60s
// default - 8MB stays comfortably inside that window even on a
// genuinely slow connection (roughly 32 KB/s -> ~256s, leaving margin)
// while cutting the number of round trips per file compared to the
// smaller chunks this started at.
const CHUNK_SIZE_BYTES = 8 * 1024 * 1024; // 8MB

/**
 * @param {File} file
 * @param {object} options
 * @param {number|string} options.assignmentId
 * @param {string} options.regNo - candidate registration number
 * @param {string} options.fileName - name to store the file under
 * @param {string} options.token - JWT, same one the axios interceptor attaches elsewhere -
 *   tus-js-client uses its own XHR layer, not axios, so this has to be passed explicitly.
 * @param {(fraction: number) => void} [options.onProgress] - 0..1
 * @returns {Promise<void>}
 */
export function uploadCandidateWorkResumable(file, { assignmentId, regNo, fileName, token, onProgress }) {
  return new Promise((resolve, reject) => {
    const documentType = `Student_${regNo}_Work`;
    const params = new URLSearchParams({
      assignmentId: String(assignmentId),
      documentType,
      studentId: regNo,
      fileName,
    });

    const upload = new Upload(file, {
      // Trailing slash before the query string matters: nginx's
      // `location /api/tus-uploads/` block only matches requests whose path
      // actually starts with that trailing slash. Without it, the request
      // falls through and gets a 301 (verified live) instead of reaching
      // the backend - tus-js-client won't follow that automatically.
      endpoint: `/api/tus-uploads/?${params.toString()}`,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      // Sent on creation and stored by the server against the upload, so
      // every later PATCH request (which carries none of the query params
      // above - only the generated per-upload URL) can still be
      // re-authorized and routed correctly. See
      // CadResumableUploadController.resolveMetadata.
      metadata: {
        assignmentId: String(assignmentId),
        documentType,
        studentId: regNo,
        fileName,
      },
      chunkSize: CHUNK_SIZE_BYTES,
      retryDelays: [0, 1000, 3000, 5000, 10000],
      storeFingerprintForResuming: true,
      removeFingerprintOnSuccess: true,
      onError: (error) => reject(error),
      onProgress: (bytesUploaded, bytesTotal) => {
        onProgress?.(bytesTotal > 0 ? bytesUploaded / bytesTotal : 0);
      },
      onSuccess: () => resolve(),
    });

    upload
      .findPreviousUploads()
      .then((previousUploads) => {
        if (previousUploads.length > 0) {
          upload.resumeFromPreviousUpload(previousUploads[0]);
        }
        upload.start();
      })
      .catch(() => {
        // Fingerprint lookup itself failing (e.g. storage disabled) isn't
        // fatal - just start fresh rather than resuming.
        upload.start();
      });
  });
}
